/**
 * Inventory tab: a searchable, paginated table of dealership inventory with
 * multi-select (checkbox) selection. Columns mirror the Inventory Management
 * page: Photos, Stock ID, VIN, Condition, Year, Make, Model, Trim, Title,
 * Exterior/Interior Color, Body Style, and a sticky Action column whose "View"
 * link opens the shared VehicleInventoryDetailsDrawer.
 *
 * A "Columns" control above the table toggles individual column visibility.
 *
 * @module vehicleModal/components/InventoryVehicleSelector
 */
import { SearchOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Image, Input, Popover, Tag } from "antd";
import React, { useMemo, useState } from "react";
import CustomTable from "../../../commonComponents/table";
import VehicleInventoryDetailsDrawer from "../../../inventory/VehicleInventoryDetailsDrawer";
import useInventoryVehicles from "../hooks/useInventoryVehicles";

const GENERIC_CAR_IMG =
  "https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png";

/** Compose a human title the same way VehicleInventoryDetailsDrawer does. */
const vehicleTitle = record =>
  [record?.year, record?.make, record?.model, record?.trim].filter(Boolean).join(" ") || "—";

/**
 * @param {Object} props
 * @param {boolean} props.active - whether the Inventory tab is shown (gates query)
 * @param {number[]} props.selectedIds - currently selected inventory row ids
 * @param {(vehicles: import("../types").InventoryVehicle[]) => void} props.onSelect
 */
export default function InventoryVehicleSelector({ active, selectedIds = [], onSelect }) {
  const { rows, count, loading, search, setSearch, page, setPage, pageSize, setPageSize } =
    useInventoryVehicles({ enabled: active });

  // Vehicle details drawer (opened from the sticky "View" action).
  const [detailsVehicle, setDetailsVehicle] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const openDetails = record => {
    setDetailsVehicle(record);
    setDetailsOpen(true);
  };
  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsVehicle(null);
  };

  // All available columns. `key` doubles as the show/hide toggle id.
  const allColumns = useMemo(
    () => [
      {
        title: "Photos",
        dataIndex: "vehicleInventoryMedias",
        key: "photos",
        width: 110,
        align: "center",
        render: (medias = []) => (
          <div className="flex flex-row items-center gap-1 justify-center">
            <Image
              preview={false}
              fallback={GENERIC_CAR_IMG}
              src={medias?.[0]?.mediaUrl || GENERIC_CAR_IMG}
              alt="vehicle"
              width={48}
              height={36}
              style={{ objectFit: "cover", borderRadius: 4 }}
            />
            {medias?.length ? (
              <span className="text-[#1677FF] text-xs font-medium">{medias.length}</span>
            ) : null}
          </div>
        ),
      },
      {
        title: "Stock ID",
        dataIndex: "stockNumber",
        key: "stockNumber",
        width: 140,
        render: v => <span className="font-medium">{v || "—"}</span>,
      },
      {
        title: "VIN",
        dataIndex: "vin",
        key: "vin",
        width: 180,
        render: v => <span className="font-mono text-sm">{v || "—"}</span>,
      },
      {
        title: "Condition",
        dataIndex: "isNew",
        key: "condition",
        width: 110,
        render: isNew => <Tag color={isNew ? "green" : "orange"}>{isNew ? "New" : "Used"}</Tag>,
      },
      { title: "Year", dataIndex: "year", key: "year", width: 90, align: "center" },
      { title: "Make", dataIndex: "make", key: "make", width: 120 },
      { title: "Model", dataIndex: "model", key: "model", width: 120 },
      {
        title: "Trim",
        dataIndex: "trim",
        key: "trim",
        width: 180,
        ellipsis: true,
        render: v => v || "—",
      },
      {
        title: "Title",
        key: "title",
        width: 220,
        ellipsis: true,
        render: (_, record) => vehicleTitle(record),
      },
      {
        title: "Exterior Color",
        dataIndex: "exteriorColor",
        key: "exteriorColor",
        width: 150,
        ellipsis: true,
        render: v => v || "—",
      },
      {
        title: "Interior Color",
        dataIndex: "interiorColor",
        key: "interiorColor",
        width: 150,
        ellipsis: true,
        render: v => v || "—",
      },
      {
        title: "Body Style",
        dataIndex: "bodyStyle",
        key: "bodyStyle",
        width: 160,
        ellipsis: true,
        render: v => v || "—",
      },
      {
        title: "Action",
        key: "action",
        width: 90,
        align: "center",
        fixed: "right",
        render: (_, record) => (
          <span
            className="text-[#1677FF] cursor-pointer hover:underline text-sm font-medium"
            onClick={e => {
              e.stopPropagation();
              openDetails(record);
            }}
          >
            View
          </span>
        ),
      },
    ],
    []
  );

  // Columns the user can show/hide (everything except the always-on Action).
  const toggleableColumns = useMemo(() => allColumns.filter(c => c.key !== "action"), [allColumns]);

  // Hidden column keys. Default: all visible.
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const toggleColumn = (key, checked) =>
    setHiddenKeys(prev => (checked ? prev.filter(k => k !== key) : [...prev, key]));

  const visibleColumns = useMemo(
    () => allColumns.filter(c => c.key === "action" || !hiddenKeys.includes(c.key)),
    [allColumns, hiddenKeys]
  );

  const columnsPopover = (
    <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
      {toggleableColumns.map(c => (
        <Checkbox
          key={c.key}
          checked={!hiddenKeys.includes(c.key)}
          onChange={e => toggleColumn(c.key, e.target.checked)}
        >
          {c.title}
        </Checkbox>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between gap-2">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-[#94A3B8]" />}
          placeholder="Search by stock id, VIN, year, make, model, trim, style, mileage"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-md"
        />
        <Popover
          content={columnsPopover}
          title="Show / hide columns"
          trigger="click"
          placement="bottomRight"
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Popover>
      </div>
      <CustomTable
        columns={visibleColumns}
        data={rows}
        rowKey={record => record.id}
        loading={loading}
        scroll={{ x: "max-content" }}
        rowSelection={{
          type: "checkbox",
          // Keep selections made on other pages even when the visible page's
          // rows change (otherwise paginating drops earlier picks).
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedIds,
          onChange: (_keys, selectedRows) => onSelect(selectedRows || []),
        }}
        pagination={{
          current: page,
          pageSize,
          total: count,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            if (nextSize !== pageSize) setPageSize(nextSize);
          },
        }}
      />
      <VehicleInventoryDetailsDrawer
        open={detailsOpen}
        onClose={closeDetails}
        vehicleData={detailsVehicle}
      />
    </div>
  );
}
